import React, { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles, TextField, Button, Link, Theme, Typography, CircularProgress } from '@material-ui/core';
import { useAppState } from '../../../state';
import { useIntl } from 'react-intl';
import moment from 'moment-timezone';
import { trackComponentEvent } from '../../../utils/tracking';
import useDeviceCheck, { DeviceCheckError } from './useDeviceCheck';
import { getMediaErrorTranslationId } from '../../../utils/mediaErrorMessages';
import { detectPermissionHelpBrowser, getPermissionHelpLinks } from '../../../utils/permissionHelpLinks';
import { SuccessIcon } from '../../../icons/SuccessIcon';
import ErrorIcon from '../../../icons/ErrorIcon';

// Persisted in sessionStorage (not component state) because "Try again" is a
// full page reload — it wipes all in-memory state, so this is the only way to
// know "this is the Nth consecutive failed retry" across reloads. Resets on
// a new tab/session, which is the right lifetime: a user coming back later
// should get a clean slate, not an escalation left over from days ago.
const PERMISSION_RETRY_STORAGE_KEY = 'preflightPermissionDeniedAttempts';

// After this many consecutive permission-denied failures with the live
// permission state still reporting 'prompt' (i.e. not a confirmed explicit
// "Block", which is detected directly — see strongBlockSignal below), treat
// it the same as a confirmed denial. This is a heuristic backstop for the one
// case we can't observe directly: the browser silently throttling repeated
// requests after the dialog was dismissed/ignored rather than explicitly
// decided. Not based on measured retry-success data — a cheap, conservative
// default chosen to stop offering a dead-end reload loop.
const PERMISSION_RETRY_BACKSTOP = 2;

function readStoredRetryCount(): number {
  const parsed = Number(sessionStorage.getItem(PERMISSION_RETRY_STORAGE_KEY));
  return Number.isFinite(parsed) ? parsed : 0;
}

// One analytics label per check outcome. Sentry only ever sees the failures;
// this is what gives the denial rate a denominator in the same place as the
// rest of the funnel.
export function deviceCheckOutcome(cameraError: DeviceCheckError | null, micError: DeviceCheckError | null) {
  const cameraDenied = cameraError?.type === 'permissionDenied';
  const micDenied = micError?.type === 'permissionDenied';
  if (cameraDenied && micDenied) return 'both_denied';
  if (cameraDenied) return 'camera_denied';
  if (micDenied) return 'microphone_denied';
  if (cameraError || micError) return 'device_error';
  return 'granted';
}

const useStyles = makeStyles((theme: Theme) => ({
  continueButton: {
    borderRadius: '100px',
    background: '#006D44',
    '&:hover': {
      background: '#006D44',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    textAlign: 'center',
  },
  textField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#006D44',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#006D44',
      },
    },
    '& .MuiInputLabel-outlined': {
      '&.Mui-focused': {
        color: '#006D44',
      },
    },
  },
  // Title and date read as a header over the left-aligned block below only
  // with some air between them; the form's own gap alone is too tight.
  heading: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '8px',
  },
  deviceCheckResults: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  deviceStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    // SuccessIcon is 28px and ErrorIcon 14px by default — same box for both
    // so the labels line up whichever state a row is in.
    '& > svg': {
      flex: 'none',
      width: '20px',
      height: '20px',
    },
  },
  deviceStatusLabel: {
    fontSize: '16px',
    fontWeight: 500,
  },
  primingText: {
    color: theme.palette.text.secondary,
    fontSize: '14px',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '14px',
  },
  secondaryButton: {
    color: '#006D44',
  },
  hintCard: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    textAlign: 'left',
    background: '#F0EEE6',
    borderRadius: '16px',
    padding: '16px',
    margin: '8px 0',
  },
  // The icon SVG is a fixed 14px and would otherwise shrink as a flex child;
  // this scales it to match the row icons' visual weight and keeps it put.
  hintIcon: {
    flex: 'none',
    display: 'flex',
    width: '20px',
    height: '20px',
    marginTop: '2px',
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
  hintBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: '1 1 auto',
    minWidth: 0,
  },
  hintTitle: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  hintText: {
    color: theme.palette.text.secondary,
    fontSize: '14px',
    lineHeight: 1.5,
  },
  hintFooter: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  hintLink: {
    color: '#006D44',
    fontSize: '14px',
    fontWeight: 500,
  },
  hintButton: {
    color: '#006D44',
    marginLeft: 'auto',
  },
}));

interface RoomNameScreenProps {
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export default function RoomNameScreen({ handleSubmit }: RoomNameScreenProps) {
  const intl = useIntl();
  const classes = useStyles();
  const { user, setUser, call } = useAppState();
  const { status, cameraError, micError, permissionState, permissionStateLoaded, requestAccess } = useDeviceCheck();
  const [retryCount, setRetryCount] = useState(readStoredRetryCount);

  // Intentional: requires BOTH to be explicitly 'granted'. On Safari/older Firefox
  // the mic permission query fails → 'unsupported', so an already-granted user
  // there falls to the two-step priming flow rather than the one-click fast path.
  // That's the deliberately conservative choice: when mic state is unknowable, we'd
  // rather show priming (one extra click) than risk auto-firing an OS prompt on a
  // user who hasn't actually granted — which is exactly what the priming pattern exists to avoid.
  const bothAlreadyGranted = permissionState?.camera === 'granted' && permissionState?.microphone === 'granted';

  // A returning visitor who already explicitly blocked access — the browser
  // won't show a dialog no matter what we do, so there's nothing to prime for
  // here either; requestAccess() is safe to auto-fire since it won't surface
  // any UI, and doing so reuses the existing "blocked" result screen below
  // instead of needing a separate one.
  const eitherAlreadyDenied = permissionState?.camera === 'denied' || permissionState?.microphone === 'denied';

  // A prior "Try again" click already was the user's explicit ask to retry —
  // on the reload it causes, re-attempt immediately rather than making them
  // click Continue a second time just to retry the same thing.
  const hasPriorAttempt = retryCount > 0;

  useEffect(() => {
    // No system permission dialog will show in any of these cases, so there's
    // nothing to prime the user for — request access as soon as we know,
    // without waiting for the Continue click.
    if (status === 'idle' && (bothAlreadyGranted || eitherAlreadyDenied || hasPriorAttempt)) {
      // requestAccess() is designed to never reject, but this call isn't
      // awaited (it can't be, inside an effect) — keep a defensive .catch so
      // no future change can leak an unhandled promise rejection here.
      requestAccess().catch(() => {});
    }
  }, [status, bothAlreadyGranted, eitherAlreadyDenied, hasPriorAttempt, requestAccess]);

  // permissionStateLoaded (not permissionState !== null) is the right gate here:
  // permissionState is also null when the Permissions API is entirely
  // unsupported, which would otherwise suppress priming copy forever on those
  // browsers rather than just during the brief initial load.
  const showPriming =
    status === 'idle' && permissionStateLoaded && !bothAlreadyGranted && !eitherAlreadyDenied && !hasPriorAttempt;

  // Guidance message(s): when BOTH are permission-denied, show the single combined
  // wording ("Camera and microphone access is blocked...") rather than two redundant
  // lines. Otherwise each device that has an error gets its own, correctly-attributed
  // message (e.g. only "Camera access is blocked..." when just the camera is denied).
  const bothPermissionDenied = cameraError?.type === 'permissionDenied' && micError?.type === 'permissionDenied';

  // A permission denial on either device counts as "the check failed" for
  // retry counting and auto-recovery below — but see micDenied for what
  // actually blocks joining. Other device-check failures (no camera found,
  // device in use, etc.) aren't helped by a reload, so those keep the
  // "warn but let them join" behavior; Zoom's own startVideo() surfaces its
  // own error if still broken.
  const permissionDenied = cameraError?.type === 'permissionDenied' || micError?.type === 'permissionDenied';

  // Only a MICROPHONE denial blocks joining. A call without audio is
  // pointless, but a call without the customer's camera is still a call: the
  // travel expert can hear them, and the camera can be allowed later, even
  // mid-call. Production telemetry showed roughly 4 in 5 permission denials
  // are camera-only, so the previous hard stop on either device was turning
  // away customers who could have joined.
  const micDenied = micError?.type === 'permissionDenied';
  const cameraOnlyDenied = cameraError?.type === 'permissionDenied' && !micDenied;
  const micAlreadyDenied = permissionState?.microphone === 'denied';

  // Official "how to allow it" page for this browser, in the app's language.
  // One link, no browser-specific copy: the hint text itself is the same
  // everywhere, and the vendor's page carries the detail.
  const helpBrowser = useMemo(() => detectPermissionHelpBrowser(), []);
  const helpLinks = getPermissionHelpLinks(helpBrowser, intl.locale);
  const helpLink = helpLinks.browserSettings ?? helpLinks.systemSettings;

  // Edge-triggered on purpose: only the false→true transition should ever
  // prompt a recovery attempt. Without this, a result screen that's merely
  // *consistent* with "granted" (e.g. a permissionDenied error alongside an
  // otherwise-granted permissionState — which real browsers won't produce,
  // but which a test double or a stale/racy permissionState read could)
  // would re-fire requestAccess() every time it settles back to 'done',
  // forever, since nothing about that steady state ever stops being true.
  const wasAlreadyGrantedRef = useRef(bothAlreadyGranted);

  useEffect(() => {
    // If the permission the user previously denied turns out to now be
    // granted — e.g. they fixed it in their browser's site settings while
    // this page is still open — recover automatically rather than leaving
    // them on the disabled "Join unavailable" view with no visible next
    // step. bothAlreadyGranted (not a narrower per-device check) matches the
    // same deliberately conservative bar as the initial auto-grant effect
    // above: only re-attempt once there's zero chance of surfacing an
    // unexpected prompt.
    const justBecameGranted = bothAlreadyGranted && !wasAlreadyGrantedRef.current;
    wasAlreadyGrantedRef.current = bothAlreadyGranted;

    if (status === 'done' && permissionDenied && justBecameGranted) {
      requestAccess().catch(() => {});
    }
  }, [status, permissionDenied, bothAlreadyGranted, requestAccess]);

  // A confirmed "denied" microphone state is a definitive signal a reload
  // can't fix. Repeated failures while state is still ambiguously 'prompt'
  // are the backstop for the case that signal can't see (see
  // PERMISSION_RETRY_BACKSTOP above). Both are treated identically — a hard
  // stop: the join button is disabled (not just a reload offer) and the
  // stronger "you can't join this call" messaging below applies. Only the
  // microphone matters here because only a microphone denial blocks joining.
  const strongBlockSignal = micAlreadyDenied || retryCount >= PERMISSION_RETRY_BACKSTOP;

  // Edge-triggered on the status transition into 'done', not just "is
  // status currently 'done'": eitherAlreadyDenied can also flip on its own
  // while already sitting on a 'done' result screen (e.g. the user clears
  // the permission via their browser's site settings, which the live
  // permission watcher picks up without a new requestAccess() attempt ever
  // running). Without this guard, that external change alone — with no
  // actual retry happening — would still count as another failed attempt.
  const prevStatusRef = useRef(status);

  useEffect(() => {
    const justFinished = status === 'done' && prevStatusRef.current !== 'done';
    prevStatusRef.current = status;

    if (!justFinished) return;

    trackComponentEvent('room-name', {
      action: 'device_check_result',
      atom: 'preflight',
      label: deviceCheckOutcome(cameraError, micError),
      molecule: 'room-name',
    });

    if (permissionDenied && !eitherAlreadyDenied) {
      const next = readStoredRetryCount() + 1;
      sessionStorage.setItem(PERMISSION_RETRY_STORAGE_KEY, String(next));
      setRetryCount(next);
    } else if (!permissionDenied) {
      sessionStorage.removeItem(PERMISSION_RETRY_STORAGE_KEY);
      setRetryCount(0);
    }
  }, [status, permissionDenied, eitherAlreadyDenied, cameraError, micError]);

  // Permission denials get the hint card below; every other device failure
  // (missing, busy, ...) keeps its own correctly-attributed guidance line.
  const guidanceMessageIds = Array.from(
    new Set(
      [
        cameraError && cameraError.type !== 'permissionDenied' && getMediaErrorTranslationId(cameraError.type, 'camera'),
        micError && micError.type !== 'permissionDenied' && getMediaErrorTranslationId(micError.type, 'microphone'),
      ].filter((id): id is string => Boolean(id))
    )
  );

  // One card, copy per state. The screen's primary button carries the state
  // (disabled, "Try again" or "Join without camera"), so the card only adds
  // a reload where the primary button isn't already one.
  const permissionHint = micDenied
    ? {
        titleId: bothPermissionDenied
          ? 'device_check.hint_blocked_title_both'
          : 'device_check.hint_blocked_title_microphone',
        bodyId: 'device_check.hint_blocked_body',
        ...(strongBlockSignal ? { buttonId: 'button.reload' } : {}),
      }
    : cameraOnlyDenied
    ? { titleId: 'device_check.hint_blocked_title_camera', bodyId: 'device_check.camera_only_hint', buttonId: 'button.try_again' }
    : null;

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const u = user || {};
    setUser({ ...u, displayName: event.target.value });
    trackSubmission();
  };

  const trackSubmission = () => {
    trackComponentEvent('room-name', {
      action: 'submit',
      atom: 'confirm_name_form',
      label: 'enter_your_name',
      molecule: 'room-name',
    });
  };

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    // React 16 pools SyntheticEvents — persist so `event` is still valid for
    // `handleSubmit` after the `await` below.
    event.persist();
    event.preventDefault();

    // Two-step on first grant: this click is the opt-in gesture for the priming
    // pattern, but it must not join immediately — it needs to show the
    // camera/mic access results first. So the first click only requests access
    // and stops; a second click (status no longer 'idle') actually joins.
    // Returning visitors skip this entirely — the auto-grant effect above
    // already moves status past 'idle' before they ever click anything, so
    // their first (and only) click already takes the join branch below.
    if (status === 'idle') {
      // requestAccess() is designed to never reject, but this is a form
      // submit handler React doesn't await — keep a defensive .catch so a
      // rejection here can't surface as an unhandled promise rejection.
      await requestAccess().catch(() => {});
      return;
    }

    // The Continue/Join button is disabled while 'checking', but a form can
    // still be submitted by pressing Enter in the text field regardless of
    // button state — guard here too so that doesn't join mid-check.
    if (status === 'checking') {
      return;
    }

    // A microphone denial can't be fixed by handing off to Zoom's startVideo()
    // — it would just fail the same getUserMedia() call again. The button is
    // swapped for either "Try again" or a disabled state in this case (see
    // below), but this guard is the actual enforcement: a disabled attribute
    // or a missing onClick can be removed via devtools, so joining must be
    // independently refused here too, regardless of how the submit was
    // triggered (e.g. an Enter-key submit, or a tampered button). The
    // backstop beneath even this: no client-side change can fabricate a real
    // getUserMedia() stream without actual OS/browser permission, so Zoom's
    // own startVideo() would still fail with no mic even if this guard were
    // somehow bypassed entirely. A camera-only denial deliberately passes
    // through (see micDenied above).
    if (micDenied) {
      return;
    }

    // Any other device-check outcome never blocks joining — hand off
    // regardless of whether the check succeeded, failed for a non-permission
    // reason, or the user had already declined it. Zoom's own startVideo()
    // does its own getUserMedia() call and will surface its own (now equally
    // well-classified) error if something is still wrong.
    handleSubmit(event);
  };

  const formattedDateTime = call?.dateAndTime && moment(call?.dateAndTime)
    .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
    .format(
      Intl.DateTimeFormat()
        .resolvedOptions()
        .locale.startsWith('en')
        ? 'DD MMM YYYY, hh:mm A'
        : 'DD.MM.YYYY, HH:mm'
    );

  const timezone = moment.tz(Intl.DateTimeFormat().resolvedOptions().timeZone).format('z');

  return (
    <form className={classes.form} onSubmit={handleFormSubmit}>
      <div className={classes.heading}>
        <Typography variant="h5">
          {call?.destination && call?.agentName
            ? intl.formatMessage({ id: 'call_intro' }, { destination: call?.destination, agent: call?.agentName })
            : intl.formatMessage({ id: 'page_title' })}
        </Typography>

        {call?.dateAndTime && <Typography variant="h6">{`${formattedDateTime} ${timezone}`}</Typography>}
      </div>

      {status === 'checking' && (
        <div className={classes.deviceStatusRow}>
          <CircularProgress size={20} />
          <Typography className={classes.primingText}>
            {intl.formatMessage({ id: 'device_check.requesting_access' })}
          </Typography>
        </div>
      )}

      {status === 'done' && (
        <div className={classes.deviceCheckResults}>
          {/* This is a permission check, not a preview — there's no way to hand a
              pre-acquired stream to Zoom's own startVideo(), so showing video/audio
              here would just be a preview of something that gets thrown away. A
              plain granted/blocked row per device avoids the on/off vs.
              permission-state confusion a live preview would introduce. */}
          <div className={classes.deviceStatusRow}>
            {cameraError ? <ErrorIcon /> : <SuccessIcon />}
            <Typography className={classes.deviceStatusLabel}>
              {intl.formatMessage({ id: cameraError ? 'device_check.camera_blocked' : 'device_check.camera_granted' })}
            </Typography>
          </div>
          <div className={classes.deviceStatusRow}>
            {micError ? <ErrorIcon /> : <SuccessIcon />}
            <Typography className={classes.deviceStatusLabel}>
              {intl.formatMessage({ id: micError ? 'device_check.mic_blocked' : 'device_check.mic_granted' })}
            </Typography>
          </div>

          {/* Actionable guidance, correctly attributed to whichever device(s) failed. */}
          {guidanceMessageIds.map((id) => (
            <Typography key={id} className={classes.errorText}>
              {intl.formatMessage({ id })}
            </Typography>
          ))}

          {permissionHint && (
            <div className={classes.hintCard}>
              <span className={classes.hintIcon}>
                <ErrorIcon />
              </span>
              <div className={classes.hintBody}>
                <Typography className={classes.hintTitle}>{intl.formatMessage({ id: permissionHint.titleId })}</Typography>
                <Typography className={classes.hintText}>{intl.formatMessage({ id: permissionHint.bodyId })}</Typography>
                {(helpLink || permissionHint.buttonId) && (
                  <div className={classes.hintFooter}>
                    {helpLink && (
                      <Link href={helpLink} target="_blank" rel="noopener noreferrer" className={classes.hintLink}>
                        {intl.formatMessage({ id: 'device_check.hint_link' })}
                      </Link>
                    )}
                    {permissionHint.buttonId && (
                      <Button
                        variant="text"
                        type="button"
                        size="small"
                        onClick={() => window.location.reload()}
                        className={classes.hintButton}
                      >
                        {intl.formatMessage({ id: permissionHint.buttonId })}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {showPriming && (
        <>
          <Typography variant="subtitle1">{intl.formatMessage({ id: 'device_check.priming_title' })}</Typography>
          <Typography className={classes.primingText}>
            {intl.formatMessage({ id: 'device_check.priming_body' })}
          </Typography>
        </>
      )}

      <TextField
        className={classes.textField}
        id="input-user-name"
        variant="outlined"
        size="small"
        value={user?.displayName || ''}
        onChange={handleNameChange}
        label={intl.formatMessage({ id: 'enter_your_name' })}
      />
      {status === 'done' && micDenied && strongBlockSignal ? (
        // Confirmed denial (or the retry backstop treating repeated ambiguous
        // failures the same way) — the call genuinely cannot happen from this
        // browser until the user changes the permission in their browser's
        // own settings, and no amount of reloading changes that. There's
        // nothing a click here could do, so this is fully disabled rather
        // than offering an action that wouldn't work — see the
        // handleFormSubmit guard above for why this is safe to rely on even
        // though a disabled attribute alone isn't tamper-proof. No click is
        // needed to recover either way: the auto-recovery effect above
        // notices once the live permission state actually changes and moves
        // off this screen on its own.
        <Button fullWidth variant="contained" type="button" color="primary" disabled className={classes.continueButton}>
          {intl.formatMessage({ id: 'button.join_unavailable' })}
        </Button>
      ) : status === 'done' && micDenied ? (
        // First failure only, not yet confirmed or escalated — still worth
        // one reload in case this was an accidental dismiss rather than an
        // explicit "Block", which a reload can still recover from.
        <Button
          fullWidth
          variant="contained"
          type="button"
          color="primary"
          onClick={() => window.location.reload()}
          className={classes.continueButton}
        >
          {intl.formatMessage({ id: 'button.try_again' })}
        </Button>
      ) : status === 'done' && cameraOnlyDenied ? (
        // Camera blocked, microphone fine: joining is allowed (see micDenied
        // above). The hint card carries "Try again" for the case where the
        // camera prompt was merely dismissed and a reload would re-prompt;
        // the auto-recovery effect above also swaps this for a plain
        // "Join now" the moment the camera is allowed.
        <Button
          fullWidth
          variant="contained"
          type="submit"
          color="primary"
          disabled={!user?.displayName}
          className={classes.continueButton}
        >
          {intl.formatMessage({ id: 'button.join_without_camera' })}
        </Button>
      ) : (
        <Button
          fullWidth
          variant="contained"
          type="submit"
          color="primary"
          disabled={!user?.displayName || status === 'checking'}
          className={classes.continueButton}
        >
          {/* Only switch to "Join now" once the check has actually finished
              ('done') — the button is disabled during 'checking' (see above),
              so labeling it "Join now" mid-check would claim an action that
              isn't actually available yet. */}
          {intl.formatMessage({ id: status === 'done' ? 'join_now' : 'button.continue' })}
        </Button>
      )}
    </form>
  );
}
